import React from 'react';
import { Check, X, Clock } from 'lucide-react';
import { fmtPx, fmtPct, fmtTime, setupLabel } from './RetestTag';

const Box = ({ title, children, testId }) => (
  <div className="border border-[var(--border-default)] bg-black/40 p-2.5 min-w-0" data-testid={testId}>
    <div className="text-[8px] tracking-[0.35em] uppercase text-cyan-sys mb-2">// {title}</div>
    <div className="space-y-1 text-[10px]">{children}</div>
  </div>
);

const Row = ({ k, v, cls = '', testId }) => (
  <div className="flex justify-between gap-2">
    <span className="text-[var(--txt-muted)] uppercase tracking-wider text-[9px]">{k}</span>
    <span className={`tabular-nums text-right break-words ${cls}`} data-testid={testId}>{v}</span>
  </div>
);

// stage names without bullish/bearish wording (direction only lives in an issued signal)
const NEUTRAL_STAGE = {
  APPROACHING: 'approaching', NEAR_RETEST: 'near retest', LEVEL_TOUCHED: 'level touched', RETEST_FORMING: 'retest forming',
  BULLISH_REJECTION: 'rejection candle', BEARISH_REJECTION: 'rejection candle', CONFIRMED_LONG: 'structure confirmed',
  CONFIRMED_SHORT: 'structure confirmed', BREAKOUT_CONTINUATION: 'continuation', BREAKDOWN_CONTINUATION: 'continuation',
  FAILED: 'failed', INVALIDATED: 'invalidated', EXPIRED: 'expired',
};

const EVIDENCE_LABEL = {
  candle_close: 'Completed-candle close', level_hold: 'Level hold / rejection', rsi: 'RSI turn',
  macd: 'MACD histogram', ema: 'EMA alignment / slope', volume: 'Relative volume', htf: 'HTF not conflicting',
};

export function StructureBlock({ s }) {
  return (
    <Box title="retest structure" testId="retest-structure">
      <Row k="type" v={`${s.kind.replace(/_/g, ' ')} · ${s.depth}`} testId="retest-type" />
      <Row k="status" v={setupLabel(s).text} cls={setupLabel(s).cls} testId="retest-state" />
      <Row k="engine stage" v={NEUTRAL_STAGE[s.state] || s.state} cls="text-[var(--txt-muted)]" testId="retest-engine-stage" />
      <Row k="zone" v={`${fmtPx(s.zone.low)} – ${fmtPx(s.zone.high)}`} testId="retest-zone" />
      <Row k="midpoint" v={fmtPx(s.zone.mid)} />
      <Row k="source" v={s.level_source} />
      <Row k="break" v={`${s.break_direction} @ ${fmtTime(s.origin_time)}`} />
      <Row k="dist to zone" v={`${s.distance_atr} ATR`} testId="retest-distance-atr" />
      <Row k="prior touches" v={s.touches_before} />
      <Row k="level status" v={s.level_status} />
      <Row k="move away" v={`${s.displacement_atr} ATR`} />
      <div className="text-[9px] text-[var(--txt-secondary)] pt-1 leading-snug">
        <span className="text-[var(--txt-muted)]">why: </span>{s.depth_reason}; {s.significance_reason}
      </div>
      {s.expiry_reason && <div className="text-[9px] text-zinc-400">expired: {s.expiry_reason}</div>}
      <Row k="expires" v={fmtTime(s.expires_at)} />
    </Box>
  );
}

export function ConfirmationBlock({ s }) {
  const it = s.evidence.items;
  return (
    <Box title="confirmation" testId="retest-confirmation">
      {Object.keys(EVIDENCE_LABEL).map((k) => (
        <div key={k} className="flex items-center gap-1.5" data-testid={`retest-evidence-${k}`}>
          {it[k]?.ok ? <Check size={11} className="text-buy shrink-0" /> : <X size={11} className="text-sell shrink-0" />}
          <span className={it[k]?.ok ? 'text-[var(--txt-primary)]' : 'text-[var(--txt-muted)]'}>{EVIDENCE_LABEL[k]}</span>
        </div>
      ))}
      <div className="pt-1 border-t border-[var(--border-default)] mt-1 space-y-1">
        <Row k="RSI" v={it.rsi?.value != null ? `${it.rsi.value.toFixed(1)} ${it.rsi.turning ? '↗ turning' : '↘ not turning'}` : 'n/a'} />
        <Row k="MACD hist" v={it.macd ? `${fmtPx(it.macd.hist)} ${it.macd.improving ? '(improving)' : '(weakening)'}` : 'n/a'} />
        <Row k="EMA20 / 50" v={it.ema ? `${fmtPx(it.ema.ema20)} / ${fmtPx(it.ema.ema50)}` : 'n/a'} />
        <Row k="rel volume" v={it.volume?.rel_volume != null ? `${it.volume.rel_volume.toFixed(2)}x` : 'n/a'} />
        <Row k="ATR" v={`${fmtPx(s.atr)} (${s.atr_pct}%)`} />
        <Row k="HTF" v={`${s.htf.status}${s.htf.reason ? ` · ${s.htf.reason}` : ''}`}
             cls={s.htf.status === 'CONFLICT' ? 'text-sell' : s.htf.status === 'ALIGNED' ? 'text-buy' : ''} testId="retest-htf" />
        <Row k="passed" v={`${s.evidence.passed.length}/7`} cls={s.evidence.ok ? 'text-buy' : 'text-amber-300'} testId="retest-passed-count" />
        {s.evidence.missing.length > 0 && (
          <div className="text-[9px] text-amber-300/80" data-testid="retest-missing">missing: {s.evidence.missing.map((m) => EVIDENCE_LABEL[m]).join(', ')}</div>
        )}
      </div>
    </Box>
  );
}

export function TradePlanBlock({ s }) {
  const p = s.plan;
  if (!p.available) {
    return (
      <Box title="trade plan" testId="retest-plan">
        <div className="text-zinc-400" data-testid="retest-plan-unavailable">NOT AVAILABLE — {p.reason}</div>
      </Box>
    );
  }
  return (
    <Box title={s.signal?.actionable ? 'trade plan (analytical — not a position)' : 'plan if it confirms (hypothetical)'} testId="retest-plan">
      <Row k="entry status" v={p.entry_status.replace(/_/g, ' ')} cls={p.entry_status.startsWith('CONFIRMED') ? 'text-buy' : 'text-amber-300'} testId="retest-entry-status" />
      <Row k="trigger" v={p.entry_trigger} />
      <Row k="entry" v={`${fmtPx(p.entry)} (${fmtPct(p.entry_dist_pct)})`} testId="retest-entry" />
      <Row k="entry zone" v={`${fmtPx(p.entry_zone[0])} – ${fmtPx(p.entry_zone[1])}`} />
      <Row k="stop / invalid." v={`${fmtPx(p.stop)} (${fmtPct(p.stop_dist_pct)})`} cls="text-sell" testId="retest-stop" />
      <div className="text-[9px] text-[var(--txt-muted)]">stop basis: {p.stop_basis} − {p.stop_buffer_atr} ATR buffer</div>
      {p.targets.length === 0 && <div className="text-zinc-400" data-testid="retest-targets-unavailable">TARGETS NOT AVAILABLE</div>}
      {p.targets.map((t, i) => (
        <div key={i} className="flex justify-between gap-2" data-testid={`retest-tp-${i + 1}`}>
          <span className="text-buy text-[9px] tracking-widest">TP{i + 1}</span>
          <span className="tabular-nums">{fmtPx(t.price)} <span className="text-[var(--txt-muted)]">({fmtPct(t.dist_pct)})</span></span>
          <span className={`tabular-nums ${t.rr >= 1.5 ? 'text-buy' : t.rr >= 1 ? 'text-amber-300' : 'text-sell'}`}>{t.rr}R</span>
        </div>
      ))}
      {p.targets.map((t, i) => (
        <div key={`n${i}`} className="text-[9px] text-[var(--txt-muted)]">TP{i + 1}: {t.note}</div>
      ))}
      {p.reject_reasons.length > 0 && (
        <div className="pt-1 border-t border-[var(--border-default)] mt-1" data-testid="retest-reject-reasons">
          {p.reject_reasons.map((r) => <div key={r} className="text-[9px] text-amber-300/90">• {r}</div>)}
        </div>
      )}
    </Box>
  );
}

export function ScoreBlock({ s }) {
  const sc = s.score;
  return (
    <Box title={`${sc.name} score`} testId="retest-score">
      <div className="text-2xl font-mono text-cyan-sys tabular-nums" data-testid="retest-score-value">{sc.value}<span className="text-[10px] text-[var(--txt-muted)]">/100</span></div>
      {Object.entries(sc.components).map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="w-28 text-[9px] uppercase tracking-wider text-[var(--txt-muted)] truncate">{k.replace(/_/g, ' ')}</span>
          <div className="flex-1 h-1 bg-[var(--border-default)]">
            <div className="h-1 bg-cyan-sys/80 transition-[width] duration-500" style={{ width: `${(v / sc.weights[k]) * 100}%` }} />
          </div>
          <span className="w-10 text-right tabular-nums text-[9px]">{v}/{sc.weights[k]}</span>
        </div>
      ))}
      <div className="text-[9px] text-[var(--txt-muted)] pt-1">{sc.note}</div>
    </Box>
  );
}

export function HistoryTimeline({ s }) {
  const h = s.state_history || [];
  return (
    <Box title="state history" testId="retest-history">
      {h.length === 0 && <div className="text-[var(--txt-muted)]">no persisted history yet</div>}
      <ol className="relative border-l border-[var(--border-default)] ml-1 space-y-2">
        {[...h].reverse().map((e, i) => (
          <li key={i} className="pl-3 relative" data-testid={`retest-history-item-${i}`}>
            <span className={`absolute -left-[4px] top-1 w-[7px] h-[7px] rounded-full ${i === 0 ? 'bg-cyan-sys' : 'bg-[var(--border-default)]'}`} />
            <div className="text-[10px] text-sky-200">{NEUTRAL_STAGE[e.state] || (e.state || '').toLowerCase()}</div>
            <div className="text-[9px] text-[var(--txt-muted)] flex items-center gap-1"><Clock size={9} />{fmtTime(e.at)}{e.from ? ` · from ${NEUTRAL_STAGE[e.from] || e.from}` : ''}</div>
            <div className="text-[9px] text-[var(--txt-secondary)] leading-snug">{e.reason}</div>
          </li>
        ))}
      </ol>
      {s.created_at && <div className="text-[9px] text-[var(--txt-muted)] pt-1">first seen {fmtTime(s.created_at)}</div>}
    </Box>
  );
}
